import React, { useEffect, useState } from "react";
import { Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  Variant,
  LabTestType,
  SelectedGroupTests,
  LabTestListItem,
  IncludedTestOptions,
} from "../../services/api/LaborderService/types";
import { LabService } from "../../services/api/LaborderService/lab.service";

interface VariantSectionProps {
  variants: Variant[];
  setVariants: React.Dispatch<React.SetStateAction<Variant[]>>;
  selectedGroupTests: SelectedGroupTests[];
  setSelectedGroupTests: React.Dispatch<
    React.SetStateAction<SelectedGroupTests[]>
  >;
  selectedSingleTests: LabTestType[];
  setSelectedSingleTests: React.Dispatch<React.SetStateAction<LabTestType[]>>;
  parentType: "single" | "head" | "healthPackage" | "profile";
}

const VariantSection: React.FC<VariantSectionProps> = ({
  variants,
  setVariants,
  selectedGroupTests,
  setSelectedGroupTests,
  selectedSingleTests,
  setSelectedSingleTests,
  parentType,
}) => {
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantType, setVariantType] = useState<string>("single");

  const removeVariant = (id: number) => {
    const variant = variants.find((v) => v.id === id);
    if (variant) {
      if (variant.type === "single") {
        setSelectedSingleTests(
          selectedSingleTests.filter((st) => st.id !== variant.testId),
        );
      } else {
        setSelectedGroupTests(
          selectedGroupTests.filter((st) => st.id !== variant.testId),
        );
      }
    }
    setVariants(variants.filter((v) => v.id !== id));
  };

  return (
    <>
      <div className="mb-2 border-t pt-6">
        <div className="flex flex-col justify-start items-start gap-2 mb-2">
          <h3 className="text-sm font-semibold text-green-600">VARIANTS</h3>
          <p className="text-sm text-gray-600">Included Tests</p>
        </div>
        <div className="flex gap-2 mb-4">
          {(() => {
            const types = (() => {
              if (parentType === "head") return ["single"];
              if (parentType === "profile") return ["single", "head"];
              return ["single", "head", "profile"];
            })();

            return types.map((type) => (
              <button
                key={type}
                onClick={() => {
                  setVariantType(type);
                  setShowVariantModal(true);
                }}
                className="px-4 py-2 text-green-600 border border-green-600 rounded-md hover:bg-green-50"
              >
                Add {type.charAt(0).toUpperCase() + type.slice(1)} Test
              </button>
            ));
          })()}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {variants.map((variant) => (
            <div
              key={variant.id}
              className="border border-gray-200 rounded-lg p-4 relative"
            >
              <button
                onClick={() => removeVariant(variant.id)}
                className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
              >
                <Trash2 size={16} />
              </button>
              <div className="flex flex-row gap-2 items-center">
                <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mb-2">
                  <div className="text-pink-500 text-lg">👤</div>
                </div>
                <div>
                  <h4 className="font-medium text-md">
                    {variant.name}
                  </h4>
                  {variant.includedTests ? (
                    <h5 className="font-medium text-sm">
                      Including Tests : {variant.includedTests}
                    </h5>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showVariantModal && (
        <VariantModal
          onClose={() => setShowVariantModal(false)}
          setVariants={setVariants}
          variantType={variantType}
          setVariantType={setVariantType}
          selectedGroupTests={selectedGroupTests}
          setSelectedGroupTests={setSelectedGroupTests}
          selectedSingleTests={selectedSingleTests}
          setSelectedSingleTests={setSelectedSingleTests}
          parentType={parentType}
        />
      )}
    </>
  );
};

interface VariantModalProps {
  onClose: () => void;
  setVariants: React.Dispatch<React.SetStateAction<Variant[]>>;
  variantType: string;
  setVariantType: React.Dispatch<React.SetStateAction<string>>;
  selectedGroupTests: SelectedGroupTests[];
  setSelectedGroupTests: React.Dispatch<
    React.SetStateAction<SelectedGroupTests[]>
  >;
  selectedSingleTests: LabTestType[];
  setSelectedSingleTests: React.Dispatch<React.SetStateAction<LabTestType[]>>;
  parentType: "single" | "head" | "healthPackage" | "profile";
}

const VariantModal: React.FC<VariantModalProps> = ({
  onClose,
  setVariants,
  variantType,
  setVariantType,
  selectedGroupTests,
  setSelectedGroupTests,
  selectedSingleTests,
  setSelectedSingleTests,
  parentType,
}) => {
  const [availableTests, setAvailableTests] = useState<LabTestListItem[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [expandedInnerIds, setExpandedInnerIds] = useState<Set<number>>(
    new Set(),
  ); // For recursed items (Panel inside Profile)

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const availableTypes = (() => {
    if (parentType === "head") return ["single"];
    if (parentType === "profile") return ["single", "head"];
    return ["single", "head", "profile"];
  })();

  useEffect(() => {
    if (!availableTypes.includes(variantType)) {
      setVariantType(availableTypes[0]);
    }
  }, [parentType, availableTypes]);

  useEffect(() => {
    fetchTests();
  }, [variantType, currentPage, searchText]);

  const fetchTests = async () => {
    if (!variantType) return;
    setIsLoading(true);
    let variantTypex = variantType;
    if (variantType === "head") {
      variantTypex = "panel";
    }
    try {
      const response = await LabService.getLabtestOptionsListing(
        currentPage,
        10,
        variantTypex,
        searchText,
      );
      setAvailableTests(response.data.labTestDetails);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching tests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSingleTestSelection = (test: LabTestListItem) => {
    setSelectedSingleTests((prev) => {
      const exists = prev.find((t) => t.id === test.id);
      if (exists) return prev.filter((t) => t.id !== test.id);
      return [
        ...prev,
        { id: test.id, name: test.name, description: test.description },
      ];
    });
  };

  const toggleGroupSelection = (
    item: LabTestListItem,
    subItems: IncludedTestOptions[],
  ) => {
    setSelectedGroupTests((prev) => {
      const exists = prev.find((g) => g.id === item.id);
      if (exists) return prev.filter((g) => g.id !== item.id);

      const typeStr =
        typeof item.type === "object" && item.type !== null
          ? (item.type as any).name?.toLowerCase()
          : "panel";
      const validType =
        typeStr === "profile" || typeStr === "panel" ? typeStr : undefined;

      return [
        ...prev,
        {
          id: item.id,
          name: item.name,
          description: item.description,
          type: validType,
          includedTestsCount: "All",
          includedTests: subItems, // Select All
        },
      ];
    });
  };

  // Granular selection logic for 3 levels (Profile -> Panel -> Single)
  // parentId: Profile/Panel ID (Level 1)
  // subItem: Panel/Single (Level 2)
  // singleId: Optional Single ID (Level 3 - if subItem is a Panel)
  const toggleSubTestSelection = (
    parentId: number,
    subItem: IncludedTestOptions,
    singleId?: number,
  ) => {
    setSelectedGroupTests((prev) => {
      const groupIndex = prev.findIndex((g) => g.id === parentId);
      const parentInAvailable = availableTests.find((t) => t.id === parentId);

      if (!parentInAvailable) return prev;

      const typeStr =
        typeof parentInAvailable.type === "object" &&
        parentInAvailable.type !== null
          ? (parentInAvailable.type as any).name?.toLowerCase()
          : "panel";
      const validType =
        typeStr === "profile" || typeStr === "panel" ? typeStr : undefined;

      let group = prev[groupIndex];

      // If group not yet selected, initialize it with current selection
      if (groupIndex === -1) {
        group = {
          id: parentInAvailable.id,
          name: parentInAvailable.name,
          description: parentInAvailable.description,
          type: validType,
          includedTestsCount: "1",
          includedTests: [],
        };
      }

      let newIncludedTests = [...group.includedTests];

      // Logic for deep update
      if (singleId !== undefined && subItem.type === "panel") {
        // Update a single inside a panel (Level 3 update)
        // Find if this panel is already in includedTests
        const panelIndex = newIncludedTests.findIndex(
          (p) => p.id === subItem.id,
        );

        if (panelIndex === -1) {
          // Panel not yet added to selection, add it with just this single
          // We need the original panel object, but 'subItem' is already it (Locked to available items usually, but let's be safe)
          // subItem is from available data, so it has ALL singles. We need to filter it.
          const newPanel = {
            ...subItem,
            singles: (subItem as any).singles.filter(
              (s: any) => s.id === singleId,
            ),
          };
          newIncludedTests.push(newPanel);
        } else {
          // Panel exists, update its singles
          let panel = { ...newIncludedTests[panelIndex] } as any; // Cast to access singles
          const singleExists = panel.singles.find(
            (s: any) => s.id === singleId,
          );

          if (singleExists) {
            // Remove single
            panel.singles = panel.singles.filter((s: any) => s.id !== singleId);
            if (panel.singles.length === 0) {
              // If no singles left, remove panel from includedTests?
              // Yes, usually.
              newIncludedTests.splice(panelIndex, 1);
            } else {
              newIncludedTests[panelIndex] = panel;
            }
          } else {
            // Add single. We need to find the single object from subItem (source of truth)
            const sourcePanel = subItem as any;
            const singleObj = sourcePanel.singles.find(
              (s: any) => s.id === singleId,
            );
            if (singleObj) {
              panel.singles = [...panel.singles, singleObj];
              newIncludedTests[panelIndex] = panel;
            }
          }
        }
      } else {
        // Standard Level 2 update (Singles in Panel OR Panels in Profile)
        // This handles "Select SubItem" directly
        const subExistsIndex = newIncludedTests.findIndex(
          (s) => s.id === subItem.id,
        );

        if (subExistsIndex !== -1) {
          // Deselect
          newIncludedTests.splice(subExistsIndex, 1);
        } else {
          // Select
          newIncludedTests.push(subItem);
        }
      }

      if (newIncludedTests.length === 0) {
        // If nothing selected in group, remove group
        if (groupIndex !== -1) {
          return prev.filter((g) => g.id !== parentId);
        }
        return prev;
      }

      // Update group
      const updatedGroup = {
        ...group,
        includedTests: newIncludedTests,
        includedTestsCount: newIncludedTests.length.toString(),
      };

      if (groupIndex === -1) {
        return [...prev, updatedGroup];
      }

      const newPrev = [...prev];
      newPrev[groupIndex] = updatedGroup;
      return newPrev;
    });
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const toggleInnerExpand = (id: number) => {
    setExpandedInnerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const isSelected = (id: number, type: "single" | "group") => {
    if (type === "single") return selectedSingleTests.some((t) => t.id === id);
    return selectedGroupTests.some((g) => g.id === id);
  };

  // Helper to check deep partial selection state
  const isDeepSelected = (
    parentId: number,
    subId: number,
    singleId?: number,
  ) => {
    const group = selectedGroupTests.find((g) => g.id === parentId);
    if (!group) return false;

    const sub = group.includedTests.find((s) => s.id === subId);
    if (!sub) return false;

    if (singleId !== undefined && sub.type === "panel") {
      return (sub as any).singles?.some((s: any) => s.id === singleId) || false;
    }

    // If singleId not provided, we check if sub is strictly in list (meaning FULLY selected or explicitly added)
    // If it's a panel, simple presence means it's tracked, but we might want to check for "all singles selected" if we had a "select all" for inner panel.
    // For now, simpler check.
    return true;
  };

  const handleConfirm = () => {
    const singleVariants: Variant[] = selectedSingleTests.map((test) => ({
      id: Math.floor(Math.random() * 1000000),
      testId: test.id,
      type: "single",
      name: test.name,
      description: test.description,
    }));

    const groupVariants: Variant[] = selectedGroupTests.map((test) => ({
      id: Math.floor(Math.random() * 1000000),
      testId: test.id,
      type: test.type === "profile" ? "profile" : "panel",
      name: test.name,
      description: test.description,
      includedTests:
        Number(test.includedTestsCount) || test.includedTests.length,
    }));

    setVariants([...singleVariants, ...groupVariants]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-green-600">
            ADD {variantType.toUpperCase()}
          </h3>
          <button onClick={onClose}>
            <X size={28} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-6 items-end mb-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 pr-10"
                placeholder="Search by name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <svg
                className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {availableTests.map((item) => {
            const isExpanded = expandedIds.has(item.id);
            const isGroupMode =
              variantType === "head" || variantType === "profile";
            const parentSelected = isSelected(
              item.id,
              variantType === "single" ? "single" : "group",
            );

            return (
              <div
                key={item.id}
                className="border border-gray-100 rounded mb-2"
              >
                <div className="grid grid-cols-12 items-center gap-4 py-2 hover:bg-gray-50 px-2 bg-gray-50/50">
                  <div className="col-span-1 flex items-center gap-2">
                    {isGroupMode && item.includedTests.length > 0 && (
                      <button
                        onClick={() => toggleExpand(item.id)}
                        className="text-emerald-600"
                      >
                        {isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                    )}
                    <input
                      type="checkbox"
                      checked={parentSelected}
                      onChange={() => {
                        if (variantType === "single")
                          toggleSingleTestSelection(item);
                        else toggleGroupSelection(item, item.includedTests);
                      }}
                    />
                  </div>
                  <div className="col-span-4 text-sm font-medium">
                    {item.name}
                  </div>
                  <div className="col-span-7 text-xs text-gray-600">
                    {item.description}
                  </div>
                </div>

                {/* Level 2: Items inside Panel/Profile */}
                {isGroupMode && isExpanded && item.includedTests && (
                  <div className="pl-8 pr-2 py-2 space-y-1 bg-white border-t">
                    <p className="text-xs text-gray-500 mb-2 font-medium">
                      Included Tests:
                    </p>
                    {item.includedTests.map((sub: IncludedTestOptions) => {
                      const isSubSelected = isDeepSelected(item.id, sub.id);
                      const canExpandSub =
                        sub.type === "panel" &&
                        (sub as any).singles?.length > 0;
                      const isSubExpanded = expandedInnerIds.has(sub.id);

                      return (
                        <div
                          key={sub.id}
                          className="border-l-2 border-green-100 pl-4 mb-2"
                        >
                          <div className="grid grid-cols-12 items-center gap-4 py-1">
                            <div className="col-span-1 flex gap-2">
                              {canExpandSub && (
                                <button
                                  onClick={() => toggleInnerExpand(sub.id)}
                                  className="text-gray-400 hover:text-green-600"
                                >
                                  {isSubExpanded ? (
                                    <ChevronUp size={14} />
                                  ) : (
                                    <ChevronDown size={14} />
                                  )}
                                </button>
                              )}
                              <input
                                type="checkbox"
                                checked={isSubSelected}
                                onChange={() =>
                                  toggleSubTestSelection(item.id, sub)
                                }
                              />
                            </div>
                            <div className="col-span-11 text-sm">
                              {sub.name}{" "}
                              <span className="text-xs text-gray-400">
                                ({sub.type || "Test"})
                              </span>
                            </div>
                          </div>

                          {/* Level 3: Singles inside Panel (within Profile) */}
                          {canExpandSub && isSubExpanded && (
                            <div className="pl-6 pt-2 space-y-1">
                              {(sub as any).singles.map((single: any) => {
                                const isSingleSelected = isDeepSelected(
                                  item.id,
                                  sub.id,
                                  single.id,
                                );
                                return (
                                  <div
                                    key={single.id}
                                    className="grid grid-cols-12 items-center gap-4 py-1"
                                  >
                                    <div className="col-span-1">
                                      <input
                                        type="checkbox"
                                        checked={isSingleSelected}
                                        onChange={() =>
                                          toggleSubTestSelection(
                                            item.id,
                                            sub,
                                            single.id,
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="col-span-11 text-xs text-gray-600">
                                      {single.name}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-between pt-4 border-t mt-4">
          <div className="flex gap-2">
            {/* Pagination placeholder */}
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 border rounded"
            >
              Prev
            </button>
            <span className="self-center text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 border rounded"
            >
              Next
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border rounded-md">
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantSection;
